import * as angular from 'angular';
import * as _ from 'lodash';

import { Store, StoreElement } from '../../../stateManagement';
import { ComponentDefinition, Ticket, TicketStatus, TicketViewModel, IApplicationConfiguration, IDictionaryAssignableStatuses, IUserModelDto, ApplicationUserViewModel } from '../../../models';
import {
    IStoreService,
    IDateNavigationService,
    IPictureDictionary,
    IUserService,
    ICurrentApplicationService,
    ITicketViewModelService,
    ITicketService
} from '../../../services';

class TicketingStateController implements angular.IComponentController {
    isLoading: boolean;
    isLoaded: boolean;
    loadingMessage: string;
    ticketList: Ticket[];
    ticketViewModelList: TicketViewModel[];
    ticketStatusList: TicketStatus[];
    selectedTicketViewModel: TicketViewModel;
    originalTicketViewModel: TicketViewModel;
    initialRange: { beginDate: string; endDate: string; };
    config: IApplicationConfiguration;
    users: IUserModelDto[];
    userPictures: IPictureDictionary;

    private _unsubscribe: Function;

    static $inject = [
        '$q',
        'dateNavigationService',
        'ticketViewModelService',
        'ticketService',
        'storeService',
        'currentApplicationService',
        'userService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _dateNavigationService: IDateNavigationService,
        private _ticketViewModelService: ITicketViewModelService,
        private _ticketService: ITicketService,
        private _storeService: IStoreService,
        private _currentApplicationService: ICurrentApplicationService,
        private _userService: IUserService
    ) {
    }

    $onInit() {
        this.isLoading = true;
        
        // Get application config
        this.config = this._currentApplicationService.getApplicationConfiguration();

        // Set date navigation to the last 7 days
        this._dateNavigationService.getLast7Days()
            .then(data => {
                this.initialRange = {
                    beginDate: data.beginDateTime,
                    endDate: data.endDateTime,
                };
            });

        this._unsubscribe = this._storeService.register(
            store => this.reloadStore(store),
            [StoreElement.Tickets, StoreElement.TicketStatuses]
        );
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    // Setup redux to retrieve tickets and ticket statuses
    reloadStore(store: Store) {
        const { ticketStatuses, tickets } = store;

        this.ticketStatusList = _.values(ticketStatuses.dictionary);
        this.ticketList = _.values(tickets.dictionary);
        this.isLoading = ticketStatuses.isLoading || tickets.isLoading;
        this.isLoaded = ticketStatuses.isLoaded && tickets.isLoaded;

        if (tickets && tickets.isLoaded) {
            this.refreshViewModels(this.ticketList);
        }
    }

    saveTicket(ticketToSave: Ticket): angular.IPromise<void> {
        let promise: angular.IPromise<void>;
        if (ticketToSave) {
            // Children aren't in store, don't call store to update it
            if (ticketToSave.parentTicketId) {
                promise = this._ticketService
                    .updateMany([ticketToSave])
                    .then((response: any) => {
                        // Handle error
                        if (response && response.data && response.data.Message) {
                            this.resetSavedTicket();
                        }
                        this._storeService.dispatchers().tickets.getOne(ticketToSave.parentTicketId);
                    })
                    .catch(() => {
                        this.resetSavedTicket();
                    });
            // Root ticket, use the store
            } else {
                promise = this._storeService.dispatchers().tickets
                    .updateOne(ticketToSave)
                    .then((response: any) => {
                        // Handle error
                        if (response && response.data && response.data.Message) {
                            this.resetSavedTicket();
                        }
                    })
                    .catch(() => {
                        this.resetSavedTicket();
                    });
            }
        } else {
            promise = this._$q.when();
        }
        return promise;
    }

    private resetSavedTicket() {
        if (this.ticketViewModelList && this.originalTicketViewModel) {
            const foundIndex = _.findIndex(this.ticketViewModelList, ticketViewModel => ticketViewModel.model.id === this.originalTicketViewModel.model.id);
            if (foundIndex >= 0) {
                this.ticketViewModelList[foundIndex] = (Object as any).assign({}, this.originalTicketViewModel);
            }
        }
    }

    private refreshViewModels(ticketList: Ticket[]): angular.IPromise<TicketViewModel[]> {
        // Create list of user who have created, been assigned to, modified, closed and completed
        if (ticketList && ticketList.length > 0) {
            const userIdList = this._ticketViewModelService.getUniqueUserList(ticketList);

            
            const userPromises = [
                // Get users details
                this._userService.getAllUserModelsWithDetails(true, false),
                // Get user pictures
                this._userService.getPictures(userIdList),
                // Get assignable statuses
                this._ticketService.getAssignableStatuses(ticketList.map(ticket => ticket.id), false)
            ];

            // Resolve promises
            return this._$q
                .all(userPromises)
                .then(responseList => {
                    if (responseList && responseList.length === 3) {
                        this.users = (responseList[0] as IUserModelDto[]);
                        this.userPictures = (responseList[1] as IPictureDictionary);
                        const assignableStatuses = responseList[2] as IDictionaryAssignableStatuses;
                        return this.ticketViewModelList = this._ticketViewModelService.buildViewModels(
                            ticketList,
                            this._userService.mapUsers(this.users),
                            this.userPictures,
                            assignableStatuses
                        );
                    }
                });
        }
    }
}

export const wtTicketingState: ComponentDefinition = {
    componentId: 'wtTicketingState',
    controller: TicketingStateController,
    template: require('./TicketingState.component.html'),
    controllerAs: 'vm',
    bindings: {}
};